import { Box, IconButton, TableCell, Tooltip, Typography, styled } from '@mui/material';
import { CompaniesIcon } from 'assets/img/icons';
import defaultSupplyChainImage from 'assets/img/supply-chain-placeholder-image.svg';
import { StyledTableRow } from 'Components/TableBase/Table';
import { TaskProgress, ThemeTypography } from 'DesignSystem';
import React, { FC, ReactNode } from 'react';
import Moment from 'react-moment';
import Image from 'DesignSystem/DataDisplay/Image/Image';
import { Booleanish, Tbooleanish } from 'types/booleanish.types';
import FlexBox from 'Components/FlexBox';
import { IBaseProduct } from 'FeatureModules/ProductPage/types/product.types';

interface ITableRowProps {
  product: IBaseProduct;
  actions?: ITableRowAction[];
  inUse?: boolean;
  isArchived?: boolean;
  onRowClick?: (product: IBaseProduct) => void;
}

export interface ITableRowAction {
  tooltip: string;
  disableIfUsed?: boolean;
  disabledTooltip?: string;
  renderIcon: () => ReactNode;
  onClick: (product: IBaseProduct) => void;
}

const IconContainer = styled(Box)(() => ({
  marginRight: 26,
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

const Date = styled(Typography)(() => ({
  width: 140,
}));

const StyledTableCell = styled(TableCell)<{ isArchived?: Tbooleanish }>(
  ({ theme, isArchived }) => ({
    opacity: isArchived === 'true' ? 0.5 : 1,
  })
);

const TableRow: FC<ITableRowProps> = ({
  product,
  actions,
  inUse = false,
  isArchived,
  onRowClick,
}) => {
  const { title, articleNumber, latestSupplyChain, connectedPartnerCompanies, image } = product;

  return (
    <StyledTableRow hover onClick={() => (onRowClick ? onRowClick(product) : undefined)}>
      <StyledTableCell scope="row" isArchived={Booleanish(isArchived)}>
        <Box display="flex" alignItems="center">
          <Image image={image} width={44} height={44} backupImageUrl={defaultSupplyChainImage} />
          <Box ml={2}>
            <FlexBox>
              <ThemeTypography variant="ITEM_TITLE" autoOverflow maxWidth={250}>
                {title}
              </ThemeTypography>
              {articleNumber && (
                <Box ml={0.8}>
                  <ThemeTypography
                    variant="BODY_MEDIUM"
                    autoOverflow
                    maxWidth={150}
                    color="GRAY_80"
                  >
                    | {articleNumber}
                  </ThemeTypography>
                </Box>
              )}
            </FlexBox>
            {latestSupplyChain?.chainTasksDetails && (
              <TaskProgress
                totalTasks={latestSupplyChain.chainTasksDetails.total}
                completedTasks={latestSupplyChain.chainTasksDetails.completed}
              />
            )}
          </Box>
        </Box>
      </StyledTableCell>
      <StyledTableCell scope="row" isArchived={Booleanish(isArchived)}>
        <Box display="flex" alignItems="center">
          <CompaniesIcon />
          <Box ml={1}>
            <ThemeTypography variant="ITEM_SUBTITLE">
              {connectedPartnerCompanies?.length || 0}
            </ThemeTypography>
          </Box>
        </Box>
      </StyledTableCell>

      <StyledTableCell scope="row" isArchived={Booleanish(isArchived)}>
        <Date variant="subtitle2">
          <Moment format="LL">{product.modifiedTimestamp}</Moment>
        </Date>
      </StyledTableCell>
      <TableCell scope="row" align="right">
        {actions && (
          <IconContainer display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
            {actions.map((action, index) => {
              const disabled = action.disableIfUsed && inUse;
              const disabledTooltip = action.disabledTooltip || '';
              return (
                <Tooltip key={index} title={disabled ? disabledTooltip : action.tooltip}>
                  <Box component="span">
                    <StyledIconButton
                      onClick={event => {
                        event.stopPropagation();
                        action.onClick(product);
                      }}
                      disabled={disabled}
                    >
                      {action.renderIcon()}
                    </StyledIconButton>
                  </Box>
                </Tooltip>
              );
            })}
          </IconContainer>
        )}
      </TableCell>
    </StyledTableRow>
  );
};

export default TableRow;
